// CoachPushSheet — push a day's coach-prescribed strength session to Speediance.
//
// A coach session is per-date with its own exercises + explicit per-set
// weights, so this focused flow:
//   - composes the prescribed session via composeCoachSessionPayload (variant
//     per group from the operator's mapping, else the catalog's first),
//   - pushes it BY NAME ('Protocol — Coach') with no templateId (the proxy's
//     find-by-name idempotency updates the single shared coach template),
//   - surfaces any exercises that can't be pushed (free-form / unknown group).
//
// Modal chrome + focus trap: fixed overlay, Escape closes, Tab cycles inside.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../lib/api';
import { C, FONT_DISPLAY, FONT_MONO, LABEL_TRACKING, SUBLABEL_TRACKING } from '../lib/design';
import { STALE_TIME_SLOW } from '../lib/query';
import {
  ProxyError,
  catalogLibrary,
  formatSpeedianceError,
  proxyPush,
  type ProxyErrorKind,
} from '../lib/speediance-client';
import { COACH_TEMPLATE_NAME, composeCoachSessionPayload } from '../lib/speediance-coach-push';
import {
  SpeedianceConfigResponseSchema,
  SpeedianceMappingRowsSchema,
  type CoachStrengthSession,
  type SpeedianceConfigResponse,
  type SpeedianceMappingRow,
} from '../lib/types';

const FOCUSABLE_SELECTOR =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type PushState =
  | { kind: 'idle' }
  | { kind: 'pushing' }
  | { kind: 'success'; warning?: string }
  | { kind: 'error'; message: string; errKind: ProxyErrorKind | string };

export type CoachPushSheetProps = {
  open: boolean;
  onClose: () => void;
  session: CoachStrengthSession;
  /** The coach activity's free-form name, shown as the session title. */
  title: string;
};

export function CoachPushSheet({ open, onClose, session, title }: CoachPushSheetProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const [push, setPush] = useState<PushState>({ kind: 'idle' });

  const configQuery = useQuery<SpeedianceConfigResponse>({
    queryKey: ['speediance', 'config'],
    queryFn: () =>
      apiClient.get<SpeedianceConfigResponse>('/api/speediance/config', {
        schema: SpeedianceConfigResponseSchema,
      }),
    staleTime: STALE_TIME_SLOW,
    enabled: open,
  });

  const mappingQuery = useQuery<SpeedianceMappingRow[]>({
    queryKey: ['speediance', 'mapping'],
    queryFn: () =>
      apiClient.get<SpeedianceMappingRow[]>('/api/speediance/mapping', {
        schema: SpeedianceMappingRowsSchema,
      }),
    staleTime: STALE_TIME_SLOW,
    enabled: open,
  });

  const catalogQuery = useQuery({
    queryKey: ['speediance', 'catalog'],
    queryFn: catalogLibrary,
    staleTime: STALE_TIME_SLOW,
    enabled: open,
  });

  const defaultMode = configQuery.data?.default_mode ?? 1;
  const mappingRows = useMemo(() => mappingQuery.data ?? [], [mappingQuery.data]);
  const catalog = useMemo(() => catalogQuery.data ?? [], [catalogQuery.data]);

  const composition = useMemo(
    () => composeCoachSessionPayload(session, mappingRows, catalog, defaultMode),
    [session, mappingRows, catalog, defaultMode],
  );
  const pushable = composition.pushable.length;
  const loadingDeps = configQuery.isLoading || mappingQuery.isLoading || catalogQuery.isLoading;

  // Reset push state whenever the sheet (re)opens.
  useEffect(() => {
    if (open) setPush({ kind: 'idle' });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current = (document.activeElement as HTMLElement | null) ?? null;
    const raf = requestAnimationFrame(() => closeBtnRef.current?.focus());
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = dialogRef.current;
      if (!root) return;
      const focusables = Array.from(
        root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter(
        (el) =>
          !el.hasAttribute('disabled') &&
          el.tabIndex !== -1 &&
          (el.offsetParent !== null || el === document.activeElement),
      );
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (active === first || !root.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey);
      previouslyFocusedRef.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  async function onPush() {
    if (pushable === 0) return;
    setPush({ kind: 'pushing' });
    try {
      const out = await proxyPush(composition.payload);
      setPush({ kind: 'success', warning: out.warning });
    } catch (e) {
      const errKind: ProxyErrorKind | string = e instanceof ProxyError ? e.kind : 'default';
      setPush({ kind: 'error', message: (e as Error).message, errKind });
    }
  }

  const errFormatted =
    push.kind === 'error'
      ? formatSpeedianceError({
          kind: push.errKind,
          message: push.message,
          sessionName: title,
          actions: { retry: () => void onPush() },
        })
      : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Push coach session to Speediance"
      ref={dialogRef}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '40px 20px',
        overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 560,
          background: C.bg,
          border: `1px solid ${C.line}`,
          fontFamily: FONT_MONO,
          color: C.text,
        }}
      >
        <div
          style={{
            padding: '14px 18px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            borderBottom: `1px solid ${C.line}`,
          }}
        >
          <div style={{ color: C.faint, fontSize: 11, letterSpacing: LABEL_TRACKING }}>
            SPEEDIANCE · PUSH COACH SESSION
          </div>
          <button
            type="button"
            ref={closeBtnRef}
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 0,
              color: C.dim,
              fontSize: 18,
              cursor: 'pointer',
              padding: '0 4px',
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: 20 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 14 }}>
            <span
              style={{
                color: C.signal,
                fontSize: 9,
                letterSpacing: SUBLABEL_TRACKING,
                fontWeight: 700,
              }}
            >
              COACH
            </span>
            <span style={{ fontFamily: FONT_DISPLAY, fontSize: 18, color: C.text }}>{title}</span>
          </div>

          {loadingDeps ? (
            <div style={{ color: C.faint, fontSize: 11 }}>LOADING…</div>
          ) : (
            <>
              {/* Pushable exercises */}
              <div
                style={{
                  border: `1px solid ${C.line}`,
                  background: C.panel2,
                }}
              >
                {composition.pushable.length === 0 ? (
                  <div style={{ padding: '12px 14px', color: C.faint, fontSize: 11 }}>
                    None of this session's exercises has a Speediance identity — nothing to push.
                  </div>
                ) : (
                  // Render from the composition's `pushable` list, NOT a re-derived
                  // filter — so the displayed exercises always match what actually
                  // gets pushed (an unknown group_id or a no-valid-sets exercise
                  // is excluded here exactly as it is from the payload).
                  composition.pushable.map((ex, i) => (
                    <div
                      key={`${ex.name}-${i}`}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 10,
                        padding: '8px 14px',
                        borderBottom: `1px solid ${C.line}`,
                        fontSize: 11,
                      }}
                    >
                      <span style={{ color: C.text, minWidth: 0 }}>{ex.name}</span>
                      <span className="num" style={{ color: C.dim, whiteSpace: 'nowrap' }}>
                        {ex.label}
                      </span>
                    </div>
                  ))
                )}
              </div>

              {/* Skipped exercises (no Speediance identity) */}
              {composition.skipped.length > 0 && (
                <div
                  style={{
                    marginTop: 10,
                    border: `1px dashed ${C.warn}`,
                    color: C.warn,
                    padding: '10px 14px',
                    fontSize: 10,
                    lineHeight: 1.5,
                  }}
                >
                  NOT PUSHED (<span className="num">{composition.skipped.length}</span>):
                  <div style={{ marginTop: 4, color: C.dim }}>
                    {composition.skipped.map((s) => (
                      <div key={s.name}>
                        · {s.name} <span style={{ color: C.faint }}>({s.reason})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Push button + result */}
              <button
                type="button"
                onClick={() => void onPush()}
                disabled={pushable === 0 || push.kind === 'pushing'}
                style={{
                  marginTop: 14,
                  width: '100%',
                  padding: '10px 14px',
                  border: `1px solid ${pushable === 0 ? C.line : C.signal}`,
                  background: 'transparent',
                  color: pushable === 0 ? C.faint : C.signal,
                  fontSize: 11,
                  letterSpacing: LABEL_TRACKING,
                  fontWeight: 700,
                  fontFamily: FONT_MONO,
                  cursor: pushable === 0 || push.kind === 'pushing' ? 'default' : 'pointer',
                }}
              >
                {push.kind === 'pushing'
                  ? 'PUSHING…'
                  : `↗ PUSH ${pushable} EXERCISE${pushable === 1 ? '' : 'S'} TO SPEEDIANCE`}
              </button>

              {push.kind === 'success' && (
                <div
                  style={{
                    marginTop: 10,
                    color: C.signal,
                    fontSize: 10,
                    letterSpacing: SUBLABEL_TRACKING,
                    fontFamily: FONT_MONO,
                  }}
                >
                  ✓ PUSHED to "{COACH_TEMPLATE_NAME}".
                  {push.warning ? ` · ${push.warning}` : ''}
                </div>
              )}

              {errFormatted && (
                <div
                  style={{
                    marginTop: 10,
                    border: `1px dashed ${C.red}`,
                    color: C.red,
                    padding: '8px 12px',
                    fontSize: 11,
                    lineHeight: 1.5,
                  }}
                >
                  {errFormatted.copy}
                  {errFormatted.action && (
                    <button
                      type="button"
                      onClick={errFormatted.action.handler}
                      style={{
                        marginLeft: 10,
                        background: 'transparent',
                        border: `1px solid ${C.red}`,
                        color: C.red,
                        fontSize: 9,
                        letterSpacing: LABEL_TRACKING,
                        fontWeight: 700,
                        padding: '2px 8px',
                        cursor: 'pointer',
                      }}
                    >
                      {errFormatted.action.label}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
